import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
  Form,
  InputGroup,
  Pagination,
  Modal,
  ListGroup
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import firebase from "firebase/app";
import "firebase/firestore";
import { fires } from "../../firebase";
import { DataGrid } from "@mui/x-data-grid";
import { CSVLink } from "react-csv";
import { FaDownload, FaEye, FaSearch, FaFilter, FaSync, FaExclamationTriangle, FaEnvelope } from "react-icons/fa";

export default function AdminEventDetails() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Event data
  const [eventData, setEventData] = useState(null);
  const [orgData, setOrgData] = useState(null);
  
  // Orders data
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(20);
  
  // Order details modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  
  // Payout dialog
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutSummary, setPayoutSummary] = useState({
    totalAmount: 0,
    totalEgFee: 0,
    totalStripeFee: 0,
    netPayout: 0,
    ordersWithMissingFees: 0
  });
  const [processingPayout, setProcessingPayout] = useState(false);
  const [payoutReceiptForm, setPayoutReceiptForm] = useState({
    totalOrders: 0,
    totalOrderedAmount: 0,
    totalAmount: 0,
    serviceFeeCustomerPay: 0,
    paymentFeeCustomerPay: 0,
    taxAmount: 0,
    netPayout: 0
  });
  
  // Attendees data
  const [attendees, setAttendees] = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [eventForms, setEventForms] = useState([]);
  const [pricesMap, setPricesMap] = useState(new Map());
  const [ticketStats, setTicketStats] = useState({ total: 0, checkedIn: 0, withSeat: 0, byType: {}, byHour: {}, byTypePaidFree: {}, rate: 0 });
  const [couponStats, setCouponStats] = useState({});
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkSyncedCount, setBulkSyncedCount] = useState(0);
  const [bulkSyncTotal, setBulkSyncTotal] = useState(0);
  const [pastPayouts, setPastPayouts] = useState([]);
  const [pastPaidTotal, setPastPaidTotal] = useState(0);
  const [ordersNotInPayout, setOrdersNotInPayout] = useState([]);
  const [remainingFundsSummary, setRemainingFundsSummary] = useState({
    totalAmount: 0,
    totalEgFee: 0,
    totalStripeFee: 0,
    netRemaining: 0,
    orderCount: 0
  });
  
  // Email sending
  const [emailToSend, setEmailToSend] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState("");
  const [emailError, setEmailError] = useState("");
  
  // Seats that have ticketId but the ticket does not have seatId/seat
  const [seatsWithMissingTicketSeatId, setSeatsWithMissingTicketSeatId] = useState([]);
  // Seats taken: list from seats perspective with ticketId, orderId, order email, ticket userName
  const [seatsTakenList, setSeatsTakenList] = useState([]);
  // Tickets that do not have seatId/seat set
  const [ticketsWithoutSeatId, setTicketsWithoutSeatId] = useState([]);
  // Per-ticket seatId input for assign seat (key = ticketId, value = seatId string)
  const [assignSeatInputMap, setAssignSeatInputMap] = useState({});
  const [assigningSeatTicketId, setAssigningSeatTicketId] = useState(null);

  // Analytics
  const [orderAnalytics, setOrderAnalytics] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    totalEgFee: 0,
    totalStripeFee: 0,
    netPayout: 0
  });

  // Order Payments comparison
  const [orderPayments, setOrderPayments] = useState([]);
  const [orderPaymentsTotal, setOrderPaymentsTotal] = useState(0);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [paymentComparison, setPaymentComparison] = useState({
    ordersTotal: 0,
    paymentsTotal: 0,
    difference: 0,
    missingPayments: [],
    extraPayments: [],
    mismatchedPayments: [],
    paymentsNotInOrders: []
  });

  // Currency selection
  const [selectedCurrency, setSelectedCurrency] = useState('CAD');

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        setIsAdmin(true);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Error verifying admin access');
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load event data
  useEffect(() => {
    if (isAdmin && eventId) {
      loadEventData();
    }
  }, [isAdmin, eventId]);

  // Load orders when event data is loaded
  useEffect(() => {
    if (eventData) {
      loadOrders();
      loadAttendees();
      loadPastPayouts();
      
      // Set currency from event data if available
      const eventCurrency = eventData.currency || eventData.priceCurrency;
      if (eventCurrency) {
        setSelectedCurrency(eventCurrency);
      }
    }
  }, [eventData]);

  useEffect(() => {
    const totalOrderedAmount = roundTo2(payoutSummary.totalAmount || 0);
    const serviceFeeCustomerPay = roundTo2(payoutSummary.totalEgFee || 0);
    const paymentFeeCustomerPay = roundTo2(payoutSummary.totalStripeFee || 0);
    const taxAmount = 0;
    const totalAmount = roundTo2(totalOrderedAmount + taxAmount);
    const netPayout = roundTo2(totalAmount - serviceFeeCustomerPay);
    setPayoutReceiptForm({
      totalOrders: orders.length || 0,
      totalOrderedAmount,
      totalAmount,
      serviceFeeCustomerPay,
      paymentFeeCustomerPay,
      taxAmount,
      netPayout
    });
  }, [eventData, orders.length, payoutSummary.totalAmount, payoutSummary.totalEgFee, payoutSummary.totalStripeFee]);

  const roundTo2 = (value) => {
    const num = Number(value) || 0;
    return Math.round((num + Number.EPSILON) * 100) / 100;
  };

  // Calculate orders not in payout when orders or payouts change
  const calculateOrdersNotInPayout = useCallback(() => {
    // Collect all order IDs that are in payouts (excluding cancelled payouts)
    const orderIdsInPayouts = new Set();
    
    pastPayouts.forEach(payout => {
      // Skip cancelled payouts
      if (payout.status === 'cancelled') return;
      
      // Extract order IDs from payout.orders array
      if (payout.orders && Array.isArray(payout.orders)) {
        payout.orders.forEach(order => {
          if (order.id) {
            orderIdsInPayouts.add(order.id);
          }
        });
      }
    });

    // Find orders that are NOT in any payout
    const notInPayout = orders.filter(order => !orderIdsInPayouts.has(order.id));
    
    // Calculate remaining funds summary
    const summary = {
      totalAmount: 0,
      totalEgFee: 0,
      totalStripeFee: 0,
      netRemaining: 0,
      orderCount: notInPayout.length
    };

    notInPayout.forEach(order => {
      const amount = order.amount || 0;
      const isRefunded = order.status === 'refunded';
      
      // For refunded orders: service fee (EG fee) is 0, but Stripe fee still applies
      const egFee = isRefunded ? 0 : (order.fees?.egFee || 0);
      const stripeFee = order.fees?.stripeFee || 0;
      
      // For refunded orders, net is negative (only Stripe fee applies)
      const net = isRefunded ? 0 - stripeFee : (amount - egFee - stripeFee);
      
      summary.totalAmount += amount;
      summary.totalEgFee += egFee;
      summary.totalStripeFee += stripeFee;
      summary.netRemaining += net;
    });

    // Round all values
    summary.totalAmount = roundTo2(summary.totalAmount);
    summary.totalEgFee = roundTo2(summary.totalEgFee);
    summary.totalStripeFee = roundTo2(summary.totalStripeFee);
    summary.netRemaining = roundTo2(summary.netRemaining);

    setOrdersNotInPayout(notInPayout);
    setRemainingFundsSummary(summary);
  }, [orders, pastPayouts]);

  useEffect(() => {
    if (orders.length > 0) {
      calculateOrdersNotInPayout();
    }
  }, [orders, pastPayouts, calculateOrdersNotInPayout]);

  // Filter orders when search term or filter changes
  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, filterStatus]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      
      // Load event information
      const eventDoc = await fires.collection('events').doc(eventId).get();
      if (!eventDoc.exists) {
        setError('Event not found');
        setLoading(false);
        return;
      }
      
      const eventInfo = eventDoc.data();
      setEventData({
        id: eventDoc.id,
        ...eventInfo,
        createdAt: eventInfo.timestamp?.toDate?.() || new Date(),
        startDate: eventInfo.startDate?.toDate?.() || new Date(),
        endDate: eventInfo.endDate?.toDate?.() || new Date()
      });
      
      // Load organization data if available
      if (eventInfo.orgId) {
        const orgDoc = await fires.collection('groups').doc(eventInfo.orgId).get();
        if (orgDoc.exists) {
          setOrgData({
            id: orgDoc.id,
            ...orgDoc.data()
          });
        }
      }
      
    } catch (err) {
      console.error('Error loading event data:', err);
      setError('Failed to load event data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const ordersSnapshot = await fires
        .collection('orders')
        .where('eventId', '==', eventId)
        .orderBy('timestamp', 'desc')
        .get();
      
      const ordersData = [];
      for (const orderDoc of ordersSnapshot.docs) {
        const orderData = orderDoc.data();
        ordersData.push({
          id: orderDoc.id,
          ...orderData,
          createdAt: orderData.timestamp?.toDate?.() || new Date()
        });
      }
      
      setOrders(ordersData);
      calculateOrderAnalytics(ordersData);
      // Load and compare with order_payments
      await loadOrderPayments(ordersData);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders: ' + err.message);
    }
  };

  const loadOrderPayments = async (ordersData) => {
    try {
      setPaymentsLoaded(false);
      const paymentsSnapshot = await fires
        .collection('order_payments')
        .where('eventId', '==', eventId)
        .get();
      
      const paymentsData = [];
      let totalAmount = 0;
      
      for (const paymentDoc of paymentsSnapshot.docs) {
        const paymentData = paymentDoc.data();
        const amount = paymentData.amount || 0;
        totalAmount += amount;
        
        paymentsData.push({
          id: paymentDoc.id,
          ...paymentData,
          amount: amount,
          createdAt: paymentData.timestamp?.toDate?.() || paymentData.createdAt?.toDate?.() || new Date()
        });
      }
      
      setOrderPayments(paymentsData);
      setOrderPaymentsTotal(totalAmount);
      
      // Compare with orders
      compareOrdersWithPayments(ordersData, paymentsData, totalAmount);
      setPaymentsLoaded(true);
    } catch (err) {
      console.error('Error loading order payments:', err);
      // Don't set error state, just log it as payments might not exist for all events
      console.warn('Could not load order_payments collection:', err.message);
      setPaymentsLoaded(true); // Mark as loaded even if empty/error
      // Still compare with empty payments
      compareOrdersWithPayments(ordersData, [], 0);
    }
  };

  const compareOrdersWithPayments = (ordersData, paymentsData, paymentsTotal) => {
    // Calculate total from orders
    const ordersTotal = ordersData.reduce((sum, order) => {
      return sum + (order.amount || 0);
    }, 0);
    
    // Create maps for easier lookup
    const ordersMap = new Map();
    ordersData.forEach(order => {
      ordersMap.set(order.id, order);
    });
    
    const paymentsMap = new Map();
    paymentsData.forEach(payment => {
      const orderId = payment.orderId || payment.order_id;
      if (orderId) {
        if (!paymentsMap.has(orderId)) {
          paymentsMap.set(orderId, []);
        }
        paymentsMap.get(orderId).push(payment);
      }
    });
    
    // Find missing payments (orders without corresponding payments)
    const missingPayments = [];
    // Also track orders with payment amount mismatches
    const mismatchedPayments = [];
    
    ordersData.forEach(order => {
      const orderId = order.id;
      const orderAmount = order.amount || 0;
      const orderPayments = paymentsMap.get(orderId) || [];
      
      if (orderPayments.length === 0 && orderAmount > 0) {
        // Order has amount but no payments
        missingPayments.push({
          orderId: orderId,
          orderUniqueId: order.uniqueId || orderId,
          userEmail: order.userEmail || 'N/A',
          amount: orderAmount,
          status: order.status || 'N/A',
          createdAt: order.createdAt
        });
      } else if (orderPayments.length > 0 && orderAmount > 0) {
        // Check if payment total matches order amount
        const paymentTotal = orderPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
        if (Math.abs(paymentTotal - orderAmount) > 0.01) { // Allow small floating point differences
          mismatchedPayments.push({
            orderId: orderId,
            orderUniqueId: order.uniqueId || orderId,
            userEmail: order.userEmail || 'N/A',
            orderAmount: orderAmount,
            paymentTotal: paymentTotal,
            difference: roundTo2(paymentTotal - orderAmount),
            paymentCount: orderPayments.length,
            status: order.status || 'N/A',
            createdAt: order.createdAt
          });
        }
      }
    });
    
    // Find extra payments (payments without corresponding orders)
    const extraPayments = [];
    const paymentsNotInOrders = []; // Track which orderIds from payments are not in orders
    paymentsData.forEach(payment => {
      const orderId = payment.orderId || payment.order_id;
      if (orderId && !ordersMap.has(orderId)) {
        extraPayments.push({
          paymentId: payment.id,
          orderId: orderId,
          amount: payment.amount || 0,
          createdAt: payment.createdAt
        });
        paymentsNotInOrders.push({
          orderId: orderId,
          paymentId: payment.id,
          amount: payment.amount || 0,
          createdAt: payment.createdAt
        });
      }
    });
    
    const difference = roundTo2(paymentsTotal - ordersTotal);
    
    setPaymentComparison({
      ordersTotal: roundTo2(ordersTotal),
      paymentsTotal: roundTo2(paymentsTotal),
      difference: difference,
      missingPayments: missingPayments,
      extraPayments: extraPayments,
      mismatchedPayments: mismatchedPayments,
      paymentsNotInOrders: paymentsNotInOrders // New field to track orderIds from payments not in orders
    });
  };

  const sendEmailReport = async () => {
    if (!emailToSend || !emailToSend.trim()) {
      setEmailError("Please enter an email address");
      setEmailSuccess("");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToSend.trim())) {
      setEmailError("Please enter a valid email address");
      setEmailSuccess("");
      return;
    }

    try {
      setSendingEmail(true);
      setEmailError("");
      setEmailSuccess("");

      const adminUid = currentUser?.uid || "";
      const apiUrl = `https://v2adminapisendemailreport-650318705789.us-central1.run.app/?event_id=${eventId}&admin_uid=${adminUid}&email=${encodeURIComponent(emailToSend.trim())}`;

      const response = await fetch(apiUrl);
      const data = await response.json();

      if (data.success || data.status === "success") {
        setEmailSuccess(`Email report queued successfully! ${data.filename ? `Filename: ${data.filename}` : ""}`);
        setEmailToSend("");
      } else {
        setEmailError("Failed to send email report. Please try again.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailError("Error sending email report: " + error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const loadPastPayouts = async () => {
    try {
      const snapshot = await fires
        .collection('payouts')
        .where('eventId', '==', eventId)
        .get();

      const payouts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPastPayouts(payouts);

      const totalPaid = payouts.reduce((sum, p) => {
        if (p.status === 'cancelled') return sum;
        const amount = p?.totals?.netPayout || 0;
        return sum + (Number(amount) || 0);
      }, 0);
      setPastPaidTotal(totalPaid);
    } catch (err) {
      console.error('Error loading past payouts:', err);
    }
  };


  const loadAttendees = async () => {
    try {
      setLoadingAttendees(true);
      
      // Load all data in parallel for better performance
      const [ordersSnapshot, ticketsSnapshot, formSubmissionsSnapshot, membersSnapshot, eventFormsSnapshot, eventDoc, seatsSnapshot] = await Promise.all([
        // Get all orders for this event
        fires.collection('orders').where('eventId', '==', eventId).get(),
        
        // Get all tickets for this event
        fires.collection('events').doc(eventId).collection('tickets').get(),
        
        // Get all form submissions for this event
        fires.collection('events').doc(eventId).collection('formSubmissions').get(),
        
        // Get all members for this event
        fires.collection('events').doc(eventId).collection('members').get(),
        
        // Get event forms from subcollection
        fires.collection('events').doc(eventId).collection('forms').get(),
        
        // Get event document for prices data
        fires.collection('events').doc(eventId).get(),
        
        // Get all seats for this event
        fires.collection('events').doc(eventId).collection('seats').get()
      ]);
      
      // Create lookup maps for faster data combination
      const ordersMap = new Map();
      ordersSnapshot.docs.forEach(doc => {
        const orderData = doc.data();
        ordersMap.set(doc.id, {
          id: doc.id,
          amount: orderData.amount || 0,
          status: orderData.status || 'N/A',
          userEmail: orderData.userEmail || 'N/A',
          userName: orderData.userName ?? orderData.user_name ?? '',
          timestamp: orderData.timestamp?.toDate?.() || new Date()
        });
      });
      
      // Create forms lookup map from subcollection and store forms data
      const formsMap = new Map();
      const formsData = [];
      eventFormsSnapshot.docs.forEach(doc => {
        const formData = doc.data();
        const formInfo = {
          id: doc.id,
          title: formData.title || 'N/A'
        };
        formsMap.set(doc.id, formInfo);
        formsData.push(formInfo);
      });
      
      // Store event forms for dynamic headers
      setEventForms(formsData);
      
      // Create prices map from eventData.prices (which is already a map)
      const eventData = eventDoc.data();
      const pricesMap = new Map();
      if (eventData?.prices) {
        // eventData.prices is a map where key is ticket type
        Object.entries(eventData.prices).forEach(([ticketType, priceData]) => {
          pricesMap.set(ticketType, {
            id: ticketType,
            priceDescription: priceData.priceDescription || 'N/A'
          });
        });
      }
      setPricesMap(pricesMap);
      
      const formSubmissionsMap = new Map();
      formSubmissionsSnapshot.docs.forEach(doc => {
        const formData = doc.data();
        const orderId = formData.orderId || "";
        formSubmissionsMap.set(orderId, {
          id: doc.id,
          forms: formData.forms || [], // Array of {id, title, value}
          ...formData
        });
      });
      
      const membersMap = new Map();
      membersSnapshot.docs.forEach(doc => {
        const memberData = doc.data();
        const memberEmail = memberData.email;
        if (memberEmail) {
          membersMap.set(memberEmail.toLowerCase(), {
            id: doc.id,
            firstName: memberData.firstName || 'N/A',
            lastName: memberData.lastName || 'N/A',
            email: memberData.email || 'N/A',
            ...memberData
          });
        }
      });
      
      // Combine data from tickets with orders and form submissions
      const attendeesData = [];
      
      ticketsSnapshot.docs.forEach(ticketDoc => {
        const ticketData = ticketDoc.data();
        const orderId = ticketData.orderId;
        const order = ordersMap.get(orderId);
        const formSubmission = formSubmissionsMap.get(orderId);
        
        // Get email from ticket, form submission, or order
        const attendeeEmail = order?.userEmail;
        const member = attendeeEmail ? membersMap.get(attendeeEmail.toLowerCase()) : null;
        
        // Only include tickets that have a valid order
        if (order) {
          // Process form submissions to match with event forms
          const processedForms = [];
          if (formSubmission?.forms) {
            formSubmission.forms.forEach(submissionForm => {
              const eventForm = formsMap.get(submissionForm.id);
              if (eventForm) {
                processedForms.push({
                  id: submissionForm.id,
                  title: eventForm.title,
                  value: submissionForm.value || 'N/A'
                });
              }
            });
          }
          
          attendeesData.push({
            id: ticketDoc.id,
            orderId: orderId,
            orderStatus: order.status,
            firstName: ticketData.firstName || formSubmission?.firstName || member?.firstName || 'N/A',
            lastName: ticketData.lastName || formSubmission?.lastName || member?.lastName || 'N/A',
            email: ticketData.email || formSubmission?.email || member?.email || order?.userEmail || 'N/A',
            ticketType: ticketData.ticketType || 'N/A',
            seat: ticketData.seat || null,
            checkin: !!ticketData.checkin,
            checkinDate: ticketData.checkinTimestamp?.toDate?.() || (ticketData.checkinTimestamp ? new Date(ticketData.checkinTimestamp) : null),
            isFree: !ticketData.paymentId,
            paymentId: ticketData.paymentId || null,
            formSubmission: formSubmission || null,
            processedForms: processedForms,
            member: member || null,
            createdAt: ticketData.createdAt?.toDate?.() || order.timestamp || new Date()
          });
        }
      });
      
      // Sort by creation date
      attendeesData.sort((a, b) => b.createdAt - a.createdAt);
      
      setAttendees(attendeesData);

      // Compute ticket stats
      const stats = { total: attendeesData.length, checkedIn: 0, withSeat: 0, byType: {}, byHour: {}, byTypePaidFree: {}, rate: 0 };
      const pad = (n) => n.toString().padStart(2, '0');
      const hourKeyOf = (d) => {
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        const hour = pad(d.getHours());
        return `${year}-${month}-${day} ${hour}:00`;
      };
      attendeesData.forEach(it => {
        if (it.checkin) stats.checkedIn++;
        if (it.seat) stats.withSeat++;
        const displayName = (pricesMap.get(it.ticketType)?.priceDescription) || it.ticketType || 'N/A';
        stats.byType[displayName] = (stats.byType[displayName] || 0) + 1;
        if (!stats.byTypePaidFree[displayName]) {
          stats.byTypePaidFree[displayName] = { paid: 0, free: 0, total: 0 };
        }
        stats.byTypePaidFree[displayName].total += 1;
        if (it.isFree) stats.byTypePaidFree[displayName].free += 1; else stats.byTypePaidFree[displayName].paid += 1;
        if (it.checkinDate instanceof Date) {
          const key = hourKeyOf(it.checkinDate);
          stats.byHour[key] = (stats.byHour[key] || 0) + 1;
        }
      });
      stats.rate = stats.total > 0 ? (stats.checkedIn / stats.total) : 0;
      setTicketStats(stats);

      // Seats that have ticketId but the ticket does not show seatId/seat
      const ticketById = new Map();
      ticketsSnapshot.docs.forEach(doc => {
        const d = doc.data();
        const seatVal = d.seatId ?? d.seat ?? '';
        ticketById.set(doc.id, {
          id: doc.id,
          seatId: seatVal,
          orderId: d.orderId ?? null,
          firstName: d.firstName,
          lastName: d.lastName,
          email: d.email
        });
      });
      const mismatched = [];
      seatsSnapshot.docs.forEach(seatDoc => {
        const seatData = seatDoc.data();
        const ticketId = seatData.ticketId ?? null;
        if (!ticketId) return;
        const ticket = ticketById.get(ticketId);
        const ticketShowsSeatId = ticket && String(ticket.seatId ?? '').trim() !== '';
        if (!ticketShowsSeatId) {
          mismatched.push({
            id: seatDoc.id,
            ...seatData,
            ticketId
          });
        }
      });
      // Natural sort for seat IDs: t1, t2, ... t10 (by number part)
      const seatIdSortKey = (id) => {
        const s = String(id ?? '');
        const m = s.match(/^(\D*)(\d+)$/);
        return m ? [m[1], parseInt(m[2], 10)] : [s, 0];
      };
      const sortSeatsById = (a, b) => {
        const [pa, na] = seatIdSortKey(a.seatId ?? a.id);
        const [pb, nb] = seatIdSortKey(b.seatId ?? b.id);
        if (pa !== pb) return pa.localeCompare(pb);
        return na - nb;
      };
      mismatched.sort(sortSeatsById);
      setSeatsWithMissingTicketSeatId(mismatched);

      // Seats with status pending, reserved, or taken only. If reserved or pending, show only seat info; if taken, show ticket/order/forms.
      const takenList = [];
      const allowedStatuses = ['pending', 'reserved', 'taken'];
      seatsSnapshot.docs.forEach(seatDoc => {
        const seatData = seatDoc.data();
        const status = String(seatData.status ?? '').toLowerCase();
        if (!allowedStatuses.includes(status)) return;
        const seatName = seatData.seatName ?? seatData.name ?? seatData.seat ?? '—';
        const isReservedOrPending = status === 'reserved' || status === 'pending';
        const ticketId = seatData.ticketId ?? null;
        if (isReservedOrPending) {
          takenList.push({
            seatId: seatDoc.id,
            seatName,
            ...seatData,
            ticketId: '—',
            orderId: '—',
            orderEmail: '—',
            userName: '—',
            formValues: formsData.map(() => '—'),
            reservedOrPending: true
          });
          return;
        }
        if (!ticketId) {
          takenList.push({
            seatId: seatDoc.id,
            seatName,
            ...seatData,
            ticketId: '—',
            orderId: '—',
            orderEmail: '—',
            userName: '—',
            formValues: formsData.map(() => '—'),
            reservedOrPending: false
          });
          return;
        }
        const ticket = ticketById.get(ticketId);
        const order = ticket?.orderId ? ordersMap.get(ticket.orderId) : null;
        const userName = order ? (String(order.userName || '').trim() || order.userEmail || '—') : '—';
        const submission = formSubmissionsMap.get(ticket?.orderId);
        const formValues = formsData.map(form => submission?.forms?.find(f => f.id === form.id)?.value ?? '—');
        takenList.push({
          seatId: seatDoc.id,
          seatName,
          ...seatData,
          ticketId,
          orderId: ticket?.orderId ?? '—',
          orderEmail: order?.userEmail ?? '—',
          userName,
          formValues,
          reservedOrPending: false
        });
      });
      takenList.sort(sortSeatsById);
      setSeatsTakenList(takenList);

      // Tickets without seatId/seat (exclude cancelled ticket or order)
      const noSeatTickets = [];
      ticketsSnapshot.docs.forEach(doc => {
        const d = doc.data();
        const seatVal = String(d.seatId ?? d.seat ?? '').trim();
        if (seatVal !== '') return;
        const order = ordersMap.get(d.orderId);
        if (String(d.status || '').toLowerCase() === 'cancelled' || String(order?.status || '').toLowerCase() === 'cancelled') return;
        const userName = [d.firstName, d.lastName].filter(Boolean).join(' ').trim() || d.email || order?.userEmail || '—';
        // Keep ticket's Firestore Timestamp (createdAt or timestamp) as-is
        const timestamp = d.createdAt ?? d.timestamp ?? null;
        noSeatTickets.push({ id: doc.id, orderId: d.orderId ?? null, userName, timestamp, ...d });
      });
      setTicketsWithoutSeatId(noSeatTickets);

      // Compute coupon stats from orders
      const couponMap = {};
      ordersSnapshot.docs.forEach(orderDoc => {
        const od = orderDoc.data();
        if (od.eventId === eventId) {
          const code = od.couponCode || od.coupon || od.promo || null;
          if (code) {
            couponMap[code] = (couponMap[code] || 0) + 1;
          }
        }
      });
      setCouponStats(couponMap);
    } catch (err) {
      console.error('Error loading attendees:', err);
      setError('Failed to load attendees: ' + err.message);
    } finally {
      setLoadingAttendees(false);
    }
  };

  /**
   * Assign a seat to a ticket. Add your logic here (e.g. update ticket doc with seatId, update seat doc with ticketId).
   * @param {string} ticketId - Ticket document id (events/{eventId}/tickets/{ticketId})
   * @param {string} seatId - Seat id or seat document id to assign (e.g. from events/{eventId}/seats)
   */
  const assignSeatToTicket = async (ticketId, seatId) => {
    const sid = (seatId || '').trim();
    if (!sid) {
      setError('Please enter a seatId');
      return;
    }
    setAssigningSeatTicketId(ticketId);
    setError('');
    try {
      // --- Add your logic here ---
      // Example: update ticket with seatId/seat
      // await fires.collection('events').doc(eventId).collection('tickets').doc(ticketId).update({ seatId: sid, seat: sid });
      // Example: update seat with ticketId (if seat doc exists)
      // const seatSnap = await fires.collection('events').doc(eventId).collection('seats').doc(sid).get();
      // if (seatSnap.exists) await seatSnap.ref.update({ ticketId });
      // --- end your logic ---
      //check if seat is taken
      const seatSnap = await fires.collection('events').doc(eventId).collection('seats').doc(sid).get();
      if (seatSnap.exists) {
        const seatData = seatSnap.data();
        if (seatData.status === 'taken') {
          setError('Seat is taken');
          return;
        }
        //if seat is available, update seat and ticket
        await fires.collection('events').doc(eventId).collection('tickets').doc(ticketId).update(
          { seatId: sid, seat: sid, seated: true, seatAt: firebase.firestore.Timestamp.fromDate(new Date()) }
        );
        await fires.collection('events').doc(eventId).collection('seats').doc(sid).update({ ticketId, status: 'taken' });
        console.log('assignSeatToTicket called', { eventId, ticketId, seatId: sid });
      }
      
      // After success you may want to reload attendees so the ticket leaves "tickets without seatId" list
      await loadAttendees();
      setAssignSeatInputMap(prev => ({ ...prev, [ticketId]: '' }));
    } catch (err) {
      console.error('Error assigning seat:', err);
      setError('Failed to assign seat: ' + (err.message || err));
    } finally {
      setAssigningSeatTicketId(null);
    }
  };

  const calculateOrderAnalytics = (ordersData) => {
    const analytics = {
      totalOrders: ordersData.length,
      totalRevenue: 0,
      totalEgFee: 0,
      totalStripeFee: 0,
      netPayout: 0
    };

    ordersData.forEach(order => {
      const amount = order.amount || 0;
      const egFee = order.fees?.egFee || 0;
      const stripeFee = order.fees?.stripeFee || 0;
      
      analytics.totalRevenue += amount;
      analytics.totalEgFee += egFee;
      analytics.totalStripeFee += stripeFee;
      analytics.netPayout += (amount - egFee - stripeFee);
    });

    setOrderAnalytics(analytics);
  };

  const filterOrders = () => {
    let filtered = orders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.uniqueId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(order => order.status === filterStatus);
    }

    setFilteredOrders(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handlePayoutClick = () => {
    // Check for orders with missing fees
    const ordersWithMissingFees = orders.filter(order => hasMissingFees(order));
    
    // Calculate payout summary from all orders
    const summary = orders.reduce((acc, order) => {
      const amount = order.amount || 0;
      const isRefunded = order.status === 'refunded';
      
      // For refunded orders: service fee (EG fee) is 0, but Stripe fee still applies
      const egFee = isRefunded ? 0 : (order.fees?.egFee || 0);
      const stripeFee = order.fees?.stripeFee || 0;
      
      // If refunded, only Stripe fee applies (net is negative)
      // We still reflect original amount in totalAmount for visibility.
      const perOrderNet = isRefunded ? 0 - stripeFee : (amount - egFee - stripeFee);
      
      return {
        totalAmount: acc.totalAmount + amount,
        totalEgFee: acc.totalEgFee + egFee,
        totalStripeFee: acc.totalStripeFee + stripeFee,
        netPayout: acc.netPayout + perOrderNet,
        ordersWithMissingFees: ordersWithMissingFees.length
      };
    }, {
      totalAmount: 0,
      totalEgFee: 0,
      totalStripeFee: 0,
      netPayout: 0,
      ordersWithMissingFees: 0
    });
    
    const roundedSummary = {
      totalAmount: roundTo2(summary.totalAmount),
      totalEgFee: roundTo2(summary.totalEgFee),
      totalStripeFee: roundTo2(summary.totalStripeFee),
      netPayout: roundTo2(summary.netPayout),
      ordersWithMissingFees: summary.ordersWithMissingFees,
      pastPayoutsTotal: roundTo2(pastPaidTotal),
      remainingFunds: roundTo2(summary.netPayout - pastPaidTotal)
    };
    setPayoutSummary(roundedSummary);
    setShowPayoutModal(true);
  };

  const handleProcessPayout = async () => {
    try {
      if (payoutSummary.ordersWithMissingFees > 0) {
        alert('Cannot process payout with missing fee data.');
        return;
      }
      if (!eventId) {
        alert('Missing event ID.');
        return;
      }
      setProcessingPayout(true);

      const payoutDoc = {
        eventId: eventId,
        eventTitle: eventData?.title || 'Untitled Event',
        orgId: eventData?.orgId || null,
        currency: 'CAD',
        totals: {
          totalAmount: roundTo2(payoutSummary.totalAmount || 0),
          totalEgFee: roundTo2(payoutSummary.totalEgFee || 0),
          totalStripeFee: roundTo2(payoutSummary.totalStripeFee || 0),
          netPayout: roundTo2(payoutSummary.netPayout || 0)
        },
        numberOfOrders: orders.length,
        orders: orders.map(o => ({
          id: o.id,
          amount: roundTo2(o.amount || 0),
          egFee: roundTo2(o.fees?.egFee || 0),
          stripeFee: roundTo2(o.fees?.stripeFee || 0),
          timestamp: o.timestamp || null,
          status: o.status || 'unknown',
          isRefunded: o.status === 'refunded'
        })),
        createdBy: {
          uid: currentUser?.uid || null,
          email: currentUser?.email || null
        },
        createdAt: new Date(),
        status: 'pending'
      };

      // Write payout under the event document for easy association
      await fires
        .collection('payouts')
        .add(payoutDoc);

      setShowPayoutModal(false);
      alert('Payout record created successfully.');
    } catch (err) {
      console.error('Error creating payout record:', err);
      alert('Failed to create payout record: ' + (err?.message || 'Unknown error'));
    } finally {
      setProcessingPayout(false);
    }
  };

  const generatePayoutImage = () => {
    try {
      console.log('Generating payout image...');
      console.log('Payout summary:', payoutSummary);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      const receiptEventName = eventData?.title || 'event';
      const receiptTotalOrders = Number(payoutReceiptForm.totalOrders) || 0;
      const receiptTotalOrderedAmount = roundTo2(Number(payoutReceiptForm.totalOrderedAmount) || 0);
      const receiptServiceFeeCustomerPay = roundTo2(Number(payoutReceiptForm.serviceFeeCustomerPay) || 0);
      const receiptPaymentFeeCustomerPay = roundTo2(Number(payoutReceiptForm.paymentFeeCustomerPay) || 0);
      const receiptTaxAmount = roundTo2(Number(payoutReceiptForm.taxAmount) || 0);
      const receiptTotalAmount = roundTo2(Number(payoutReceiptForm.totalAmount) || (receiptTotalOrderedAmount + receiptTaxAmount));
      const receiptNetPayout = roundTo2(receiptTotalAmount - receiptServiceFeeCustomerPay);

      // Set canvas size
      canvas.width = 900;
      canvas.height = 750;
      
      // Professional background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#ffffff');
      gradient.addColorStop(0.1, '#f8f9fa');
      gradient.addColorStop(1, '#e9ecef');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw content immediately (without waiting for logo)
      const drawContent = () => {
        // Professional header with subtle shadow
        ctx.shadowColor = 'rgba(0,0,0,0.1)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        
        // Main title
        ctx.fillStyle = '#1a252f';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(eventData?.title || 'Event Title', canvas.width / 2, 80);
        
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        // Date with professional styling
        ctx.fillStyle = '#7f8c8d';
        ctx.font = '18px Arial';
        const eventDate = eventData?.startDateString || 'N/A';
        ctx.fillText(`Event Date: ${eventDate}`, canvas.width / 2, 130);
        
        // Professional summary box
        const boxX = 60;
        const boxY = 200;
        const boxWidth = canvas.width - 120;
        const boxHeight = 480;
        
        // Box shadow
        ctx.shadowColor = 'rgba(0,0,0,0.1)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 4;
        
        // Box background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
        
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        // Professional border
        ctx.strokeStyle = '#e1e8ed';
        ctx.lineWidth = 1;
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);
        
        // Summary items
        const items = [
          { label: 'Total Orders', value: receiptTotalOrders.toString(), color: '#3498db' },
          { label: 'Total Ordered Amount', value: formatCurrency(receiptTotalOrderedAmount), color: '#27ae60' },
          { label: 'Total Amount', value: formatCurrency(receiptTotalAmount), color: '#16a085' },
          { label: 'Service Fee (Customer Pay)', value: formatCurrency(receiptServiceFeeCustomerPay), color: '#e67e22' },
          { label: 'Payment Fee (Customer Pay)', value: formatCurrency(receiptPaymentFeeCustomerPay), color: '#d35400' },
          { label: 'Tax', value: formatCurrency(receiptTaxAmount), color: '#8e44ad' },
          { label: 'Net Payout', value: formatCurrency(receiptNetPayout), color: '#27ae60', isLarge: true }
        ];
        
        let yPos = boxY + 80;
        items.forEach((item, index) => {
          // Professional label styling
          ctx.fillStyle = '#2c3e50';
          ctx.font = 'bold 20px Arial';
          ctx.textAlign = 'left';
          ctx.fillText(item.label, boxX + 50, yPos);
          
          // Value with enhanced styling
          ctx.fillStyle = item.color;
          if (item.isLarge) {
            ctx.font = 'bold 36px Arial';
            // Add subtle shadow for payout amount
            ctx.shadowColor = 'rgba(0,0,0,0.1)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
          } else {
            ctx.font = 'bold 26px Arial';
          }
          ctx.textAlign = 'right';
          ctx.fillText(item.value, boxX + boxWidth - 50, yPos);
          
          // Reset shadow
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          // Professional separator line
          if (index < items.length - 1) {
            ctx.strokeStyle = '#f1f3f4';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(boxX + 50, yPos + 25);
            ctx.lineTo(boxX + boxWidth - 50, yPos + 25);
            ctx.stroke();
          }
          
          yPos += 70; // Increased spacing for better readability
        });
        
        // Professional footer
        ctx.fillStyle = '#7f8c8d';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`Generated on ${new Date().toLocaleString()}`, canvas.width / 2, canvas.height - 40);
        
        // Add company branding
        ctx.fillStyle = '#95a5a6';
        ctx.font = '14px Arial';
        ctx.fillText('EventGo - All Rights Reserved', canvas.width / 2, canvas.height - 20);
        
        // Convert to image and download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `payout-receipt-${receiptEventName?.replace(/[^a-zA-Z0-9]/g, '-') || 'event'}-${new Date().toISOString().split('T')[0]}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            console.log('Image generated and downloaded successfully');
          } else {
            console.error('Failed to create blob from canvas');
            alert('Failed to generate image. Please try again.');
          }
        });
      };
      
      drawContent();
      
      
    } catch (error) {
      console.error('Error generating payout image:', error);
      alert('Failed to generate image: ' + error.message);
    }
  };

  const generateRemainingFundsImage = () => {
    try {
      console.log('Generating remaining funds image...');
      console.log('Remaining funds summary:', remainingFundsSummary);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = 900;
      canvas.height = 750;
      
      // Professional background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#fff9e6');
      gradient.addColorStop(0.1, '#fff4d6');
      gradient.addColorStop(1, '#ffeaa7');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw content
      const drawContent = () => {
        // Professional header with subtle shadow
        ctx.shadowColor = 'rgba(0,0,0,0.1)';
        ctx.shadowBlur = 4;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        
        // Main title
        ctx.fillStyle = '#1a252f';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('REMAINING FUNDS SUMMARY', canvas.width / 2, 80);
        
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        // Event details with better typography
        ctx.fillStyle = '#2c3e50';
        ctx.font = 'bold 24px Arial';
        ctx.fillText(eventData?.title || 'Event Title', canvas.width / 2, 120);
        
        // Date with professional styling
        ctx.fillStyle = '#7f8c8d';
        ctx.font = '18px Arial';
        const eventDate = eventData?.startDateString || 'N/A';
        ctx.fillText(`Event Date: ${eventDate}`, canvas.width / 2, 150);
        
        // Professional summary box
        const boxX = 60;
        const boxY = 200;
        const boxWidth = canvas.width - 120;
        const boxHeight = 480;
        
        // Box shadow
        ctx.shadowColor = 'rgba(0,0,0,0.1)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 4;
        
        // Box background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
        
        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        
        // Professional border with warning color
        ctx.strokeStyle = '#ffc107';
        ctx.lineWidth = 2;
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);
        
        // Summary items
        const items = [
          { label: 'Total Payment Amount', value: formatCurrency(remainingFundsSummary?.totalAmount || 0), color: '#27ae60' },
          { label: 'Number of Orders', value: remainingFundsSummary?.orderCount?.toString() || '0', color: '#3498db' },
          { label: 'Service Fee', value: formatCurrency(remainingFundsSummary?.totalEgFee || 0), color: '#f39c12' },
          { label: 'Payment Fee (Stripe)', value: formatCurrency(remainingFundsSummary?.totalStripeFee || 0), color: '#e67e22' },
          { label: 'Total Fees', value: formatCurrency((remainingFundsSummary?.totalEgFee || 0) + (remainingFundsSummary?.totalStripeFee || 0)), color: '#d35400' },
          { label: 'Net Remaining Funds', value: formatCurrency(remainingFundsSummary?.netRemaining || 0), color: remainingFundsSummary?.netRemaining >= 0 ? '#27ae60' : '#e74c3c', isLarge: true }
        ];
        
        let yPos = boxY + 80;
        items.forEach((item, index) => {
          // Professional label styling
          ctx.fillStyle = '#2c3e50';
          ctx.font = 'bold 20px Arial';
          ctx.textAlign = 'left';
          ctx.fillText(item.label, boxX + 50, yPos);
          
          // Value with enhanced styling
          ctx.fillStyle = item.color;
          if (item.isLarge) {
            ctx.font = 'bold 36px Arial';
            // Add subtle shadow for remaining funds amount
            ctx.shadowColor = 'rgba(0,0,0,0.1)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
          } else {
            ctx.font = 'bold 26px Arial';
          }
          ctx.textAlign = 'right';
          ctx.fillText(item.value, boxX + boxWidth - 50, yPos);
          
          // Reset shadow
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;
          
          // Professional separator line
          if (index < items.length - 1) {
            ctx.strokeStyle = '#f1f3f4';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(boxX + 50, yPos + 25);
            ctx.lineTo(boxX + boxWidth - 50, yPos + 25);
            ctx.stroke();
          }
          
          yPos += 70; // Increased spacing for better readability
        });
        
        // Professional footer
        ctx.fillStyle = '#7f8c8d';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`Generated on ${new Date().toLocaleString()}`, canvas.width / 2, canvas.height - 40);
        
        // Add company branding
        ctx.fillStyle = '#95a5a6';
        ctx.font = '14px Arial';
        ctx.fillText('EventGo - All Rights Reserved', canvas.width / 2, canvas.height - 20);
        
        // Convert to image and download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `remaining-funds-${eventData?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'event'}-${new Date().toISOString().split('T')[0]}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            console.log('Remaining funds image generated and downloaded successfully');
          } else {
            console.error('Failed to create blob from canvas');
            alert('Failed to generate image. Please try again.');
          }
        });
      };
      
      drawContent();
      
    } catch (error) {
      console.error('Error generating remaining funds image:', error);
      alert('Failed to generate image: ' + error.message);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { variant: 'success', text: 'Completed' },
      pending: { variant: 'warning', text: 'Pending' },
      refunded: { variant: 'info', text: 'Refunded' },
      cancelled: { variant: 'danger', text: 'Cancelled' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const hasMissingFees = (order) => {
    const amount = order.amount || 0;
    const egFee = order.fees?.egFee || 0;
    const stripeFee = order.fees?.stripeFee || 0;
    
    // For refunded orders, ensure payment processing fee is present
    if (order.status === 'refunded') {
      return stripeFee === 0;
    }
    return amount > 0 && (egFee === 0 || stripeFee === 0);
  };

  const handleSyncFees = async (orderId, e) => {
    e.stopPropagation(); // Prevent row click
    
    // Get button elements and store original content
    const orderElement = e.target.closest('tr');
    const syncButton = orderElement.querySelector('.sync-button');
    const originalContent = syncButton.innerHTML;
    
    try {
      // Show loading state
      syncButton.innerHTML = '<i class="fas fa-sync fa-spin"></i>';
      syncButton.disabled = true;
      
      // Call the API to sync fees
      const response = await fetch(`https://v2organizerapimatchorderdetails-650318705789.us-central1.run.app/?order_id=${orderId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Sync response:', data);
      
      // Reload orders to get updated fee data
      await loadOrders();
      
      // Show success message
      alert('Fees synced successfully!');
      
    } catch (error) {
      console.error('Error syncing fees:', error);
      alert(`Failed to sync fees: ${error.message}`);
    } finally {
      // Reset button state
      syncButton.innerHTML = originalContent;
      syncButton.disabled = false;
    }
  };

  const handleBulkSyncFees = async () => {
    try {
      // Reload fees for ALL paid orders, regardless of whether fees exist
      const targetOrders = orders.filter(o => (o.amount || 0) > 0);
      if (targetOrders.length === 0) {
        alert('No paid orders found to reload fees.');
        return;
      }
      setBulkSyncing(true);
      setBulkSyncedCount(0);
      setBulkSyncTotal(targetOrders.length);
      for (const ord of targetOrders) {
        try {
          await fetch(`https://v2organizerapimatchorderdetails-650318705789.us-central1.run.app/?order_id=${ord.id}`);
          setBulkSyncedCount(prev => prev + 1);
        } catch (e) {
          console.error('Bulk fee sync failed for order', ord.id, e);
        }
      }
      await loadOrders();
      alert('Bulk fee sync completed.');
    } catch (e) {
      console.error('Error during bulk fee sync:', e);
      alert('Bulk fee sync failed: ' + (e?.message || 'Unknown error'));
    } finally {
      setBulkSyncing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedCurrency
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const generateTicketsCSVData = () => {
    // Sort attendees by check-in timestamp (checked-in first, then by timestamp descending)
    const sortedAttendees = [...attendees].sort((a, b) => {
      // If one is checked in and the other isn't, checked-in comes first
      if (a.checkin && !b.checkin) return -1;
      if (!a.checkin && b.checkin) return 1;
      
      // If both are checked in, sort by check-in timestamp (most recent first)
      if (a.checkin && b.checkin) {
        const aDate = a.checkinDate || new Date(0);
        const bDate = b.checkinDate || new Date(0);
        return bDate - aDate; // Descending order
      }
      
      // If neither is checked in, sort by registration date (most recent first)
      const aRegDate = a.createdAt || new Date(0);
      const bRegDate = b.createdAt || new Date(0);
      return bRegDate - aRegDate;
    });

    const csvData = sortedAttendees.map(attendee => {
      const baseData = {
        'Ticket ID': attendee.id,
        'Order ID': attendee.orderId,
        'First Name': attendee.firstName,
        'Last Name': attendee.lastName,
        'Email': attendee.email,
        'Ticket Type': pricesMap.get(attendee.ticketType)?.priceDescription || attendee.ticketType,
        'Seat': attendee.seat || 'No seat assigned',
        'Order Status': attendee.orderStatus,
        'Registered Date': formatDate(attendee.createdAt),
        'Check-in Timestamp': attendee.checkinDate ? formatDate(attendee.checkinDate) : '-'
      };

      // Add dynamic form columns
      eventForms.forEach(form => {
        const formSubmission = attendee.processedForms?.find(f => f.id === form.id);
        baseData[form.title] = formSubmission ? formSubmission.value : '-';
      });

      return baseData;
    });

    return csvData;
  };

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  // CSV data for export with detailed information
  const csvData = filteredOrders.map(order => {
    const amount = order.amount || 0;
    const metadata = order.viewData?.metadata || {};
    const isRefunded = order.status === 'refunded';
    // For refunded orders: service fee (EG fee) is 0, but Stripe fee still applies
    const egFee = isRefunded ? 0 : (order.fees?.egFee || 0);
    const stripeFee = order.fees?.stripeFee || 0;
    const totalFees = egFee + stripeFee;
    const netPayout = isRefunded ? 0 - stripeFee : (amount - egFee - stripeFee);
    
    // Format cart items
    let cartItemsSummary = '';
    let totalQuantity = 0;
    if (order.cartItems && Array.isArray(order.cartItems)) {
      cartItemsSummary = order.cartItems.map(item => {
        const qty = item.quantity || 1;
        totalQuantity += qty;
        return `${item.priceDescription || 'Item'} (Qty: ${qty}, Price: ${formatCurrency(item.price || 0)})`;
      }).join('; ');
    }
    
    return {
      'Order ID': order.uniqueId || order.id,
      'Internal Order ID': order.id || 'N/A',
      'User Name': order.userName || 'N/A',
      'User Email': order.userEmail || 'N/A',
      'Event ID': order.eventId || 'N/A',
      'Event Title': order.title || eventData?.title || 'N/A',
      'Status': order.status || 'N/A',
      'Is Refunded': isRefunded ? 'Yes' : 'No',
      'Amount': roundTo2(amount),
      'Service Fee (EG Fee)': roundTo2(egFee),
      'Payment Fee (Stripe Fee)': roundTo2(stripeFee),
      'Total Fees': roundTo2(totalFees),
      'Net Payout': roundTo2(netPayout),
      'Coupon Code': order.couponCode || order.coupon || order.promo || 'N/A',
      'Total Quantity': totalQuantity || 0,
      'Cart Items': cartItemsSummary || 'N/A',
      'Payment Method': order.paymentMethod || 'N/A',
      'Payment ID': order.paymentId || 'N/A',
      'UTM Source': metadata.utm_source || metadata.utmSource || 'N/A',
      'UTM Medium': metadata.utm_medium || metadata.utmMedium || 'N/A',
      'UTM Campaign': metadata.utm_campaign || metadata.utmCampaign || 'N/A',
      'UTM Term': metadata.utm_term || metadata.utmTerm || 'N/A',
      'UTM Content': metadata.utm_content || metadata.utmContent || 'N/A',
      'Created Date': formatDate(order.createdAt),
      'Created Timestamp': order.createdAt?.toISOString?.() || (order.timestamp?.toDate?.()?.toISOString?.() || 'N/A')
    };
  });

  if (loading) {
    return (
      <Container className="py-5">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You do not have permission to access this page.</p>
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate('/admin-dashboard')}>
            Back to Admin Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!eventData) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Event Not Found</Alert.Heading>
          <p>The requested event could not be found.</p>
          <Button variant="outline-warning" onClick={() => navigate('/admin-dashboard')}>
            Back to Admin Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">Event Details</h2>
              <p className="text-muted mb-0">Admin View - Event ID: {eventId}</p>
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate('/admin-dashboard')}
                className="me-2"
              >
                Back to Dashboard
              </Button>
              <Button 
                variant="outline-primary" 
                onClick={loadOrders}
                disabled={loading}
              >
                <FaSync className="me-1" />
                Refresh
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Event Information */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Event Information</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <h4 className="mb-3">{eventData.title}</h4>
                  
                  <Row className="mb-3">
                    <Col sm={6}>
                      <strong>Start Date:</strong><br />
                      <span className="text-muted">{formatDate(eventData.startDate)}</span>
                    </Col>
                    <Col sm={6}>
                      <strong>End Date:</strong><br />
                      <span className="text-muted">{formatDate(eventData.endDate)}</span>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col sm={6}>
                      <strong>Location:</strong><br />
                      <span className="text-muted">{eventData.location || 'N/A'}</span>
                    </Col>
                    <Col sm={6}>
                      <strong>Status:</strong><br />
                      <Badge bg={eventData.status === 'live' ? 'success' : 'secondary'}>
                        {eventData.status || 'Unknown'}
                      </Badge>
                    </Col>
                  </Row>
                  
                  <Row className="mb-3">
                    <Col sm={6}>
                      <strong>Display Currency:</strong><br />
                      <Form.Select
                        value={selectedCurrency}
                        onChange={(e) => setSelectedCurrency(e.target.value)}
                        style={{ maxWidth: '200px', display: 'inline-block' }}
                      >
                        <option value="USD">USD</option>
                        <option value="CAD">CAD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="AUD">AUD</option>
                        <option value="JPY">JPY</option>
                        <option value="CNY">CNY</option>
                      </Form.Select>
                      <small className="text-muted ms-2">
                        (All amounts will be displayed in {selectedCurrency})
                      </small>
                    </Col>
                  </Row>
                  
                  {orgData && (
                    <div className="mb-3">
                      <strong>Organization:</strong><br />
                      <span className="text-muted">{orgData.name || 'N/A'}</span>
                    </div>
                  )}
                </Col>
                <Col md={4}>
                  {eventData.photoURL && (
                    <img 
                      src={eventData.photoURL} 
                      alt="Event" 
                      className="img-fluid rounded"
                      style={{ maxHeight: '200px', width: '100%', objectFit: 'cover' }}
                    />
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Send Email Report */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">
                <FaEnvelope className="me-2" />
                Send Email Report
              </h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FaEnvelope />
                        </InputGroup.Text>
                        <Form.Control
                          type="email"
                          placeholder="Enter email address to send report"
                          value={emailToSend}
                          onChange={(e) => {
                            setEmailToSend(e.target.value);
                            setEmailError("");
                            setEmailSuccess("");
                          }}
                          disabled={sendingEmail}
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Enter the email address where you want to receive the event report.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end">
                    <Button
                      variant="info"
                      onClick={sendEmailReport}
                      disabled={sendingEmail || !emailToSend.trim()}
                      className="w-100"
                    >
                      {sendingEmail ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaEnvelope className="me-2" />
                          Send Report
                        </>
                      )}
                    </Button>
                  </Col>
                </Row>
                {emailSuccess && (
                  <Alert variant="success" className="mt-3 mb-0">
                    {emailSuccess}
                  </Alert>
                )}
                {emailError && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    {emailError}
                  </Alert>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Analytics */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h5 className="text-primary">{orderAnalytics.totalOrders}</h5>
              <p className="text-muted mb-0">Total Orders</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h5 className="text-success">{formatCurrency(orderAnalytics.totalRevenue)}</h5>
              <p className="text-muted mb-0">Total Revenue</p>
            </Card.Body>
          </Card>
        </Col>
        {paymentsLoaded && (
          <Col md={2}>
            <Card 
              className={`text-center border-0 shadow-sm ${
                orderPayments.length === 0 
                  ? 'bg-secondary text-white' 
                  : paymentComparison.difference === 0 
                    ? 'bg-success text-white' 
                    : 'bg-warning text-dark'
              }`}
              style={{ borderLeft: `4px solid ${
                orderPayments.length === 0 
                  ? '#6c757d' 
                  : paymentComparison.difference === 0 
                    ? '#28a745' 
                    : '#ffc107'
              }` }}
            >
              <Card.Body>
                {orderPayments.length === 0 ? (
                  <>
                    <h5 className="text-white">No Payments</h5>
                    <p className="text-white-50 mb-0" style={{ fontSize: '0.75rem' }}>
                      No order_payments found
                    </p>
                    <small className="text-white-50" style={{ fontSize: '0.7rem' }}>
                      Orders: {formatCurrency(paymentComparison.ordersTotal)}
                    </small>
                  </>
                ) : (
                  <>
                    <h5 className={paymentComparison.difference === 0 ? "text-white" : "text-dark"}>
                      {paymentComparison.difference === 0 ? '✓ Match' : formatCurrency(Math.abs(paymentComparison.difference))}
                    </h5>
                    <p className={paymentComparison.difference === 0 ? "text-white-50 mb-0" : "text-dark mb-0"} style={{ fontSize: '0.75rem' }}>
                      {paymentComparison.difference === 0 
                        ? 'Payments Match' 
                        : paymentComparison.difference > 0 
                          ? 'Payments > Orders' 
                          : 'Orders > Payments'}
                    </p>
                    {paymentComparison.difference !== 0 && (
                      <small className={paymentComparison.difference === 0 ? "text-white-50" : "text-dark"} style={{ fontSize: '0.7rem' }}>
                        Orders: {formatCurrency(paymentComparison.ordersTotal)}<br />
                        Payments: {formatCurrency(paymentComparison.paymentsTotal)}
                      </small>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}
        <Col md={2}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h5 className="text-warning">{formatCurrency(orderAnalytics.totalEgFee)}</h5>
              <p className="text-muted mb-0">Total EG Fees</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <h5 className="text-warning">{formatCurrency(orderAnalytics.totalStripeFee)}</h5>
              <p className="text-muted mb-0">Total Stripe Fees</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={paymentsLoaded ? 2 : 4}>
          <Card className="text-center border-0 shadow-sm bg-success text-white">
            <Card.Body>
              <h5 className="text-white">{formatCurrency(orderAnalytics.netPayout)}</h5>
              <p className="text-white-50 mb-0">Net Payout Amount</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Remaining Funds Section */}
      {ordersNotInPayout.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm border-warning">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">
                  <FaExclamationTriangle className="me-2" />
                  Orders Not in Payout - Remaining Funds
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Alert variant="info" className="mb-0 flex-grow-1 me-3">
                    <strong>{remainingFundsSummary.orderCount}</strong> order(s) have not been included in any payout. 
                    These orders represent <strong>{formatCurrency(remainingFundsSummary.netRemaining)}</strong> in remaining funds 
                    (after service fees and Stripe fees).
                  </Alert>
                  <Button 
                    variant="info" 
                    onClick={generateRemainingFundsImage}
                    className="flex-shrink-0"
                  >
                    <FaDownload className="me-2" />
                    Generate Image
                  </Button>
                </div>

                <Row className="mb-3">
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body>
                        <h5 className="text-primary">{formatCurrency(remainingFundsSummary.totalAmount)}</h5>
                        <p className="text-muted mb-0 small">Total Amount</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body>
                        <h5 className="text-warning">{formatCurrency(remainingFundsSummary.totalEgFee)}</h5>
                        <p className="text-muted mb-0 small">Service Fees</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body>
                        <h5 className="text-warning">{formatCurrency(remainingFundsSummary.totalStripeFee)}</h5>
                        <p className="text-muted mb-0 small">Stripe Fees</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-success text-white">
                      <Card.Body>
                        <h5 className="text-white">{formatCurrency(remainingFundsSummary.netRemaining)}</h5>
                        <p className="text-white-50 mb-0 small">Net Remaining</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="table-responsive">
                  <Table striped hover size="sm">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>User Email</th>
                        <th>Amount</th>
                        <th>Service Fee</th>
                        <th>Stripe Fee</th>
                        <th>Net Remaining</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersNotInPayout.map((order) => {
                        const amount = order.amount || 0;
                        const isRefunded = order.status === 'refunded';
                        
                        // For refunded orders: service fee (EG fee) is 0, but Stripe fee still applies
                        const egFee = isRefunded ? 0 : (order.fees?.egFee || 0);
                        const stripeFee = order.fees?.stripeFee || 0;
                        
                        // For refunded orders, net is negative (only Stripe fee applies)
                        const netRemaining = isRefunded 
                          ? roundTo2(0 - stripeFee) 
                          : roundTo2(amount - egFee - stripeFee);
                        
                        return (
                          <tr key={order.id}>
                            <td>
                              <code>{order.uniqueId || order.id}</code>
                            </td>
                            <td>{order.userEmail || 'N/A'}</td>
                            <td className="fw-bold text-success">
                              {formatCurrency(amount)}
                            </td>
                            <td className="text-warning">
                              {isRefunded ? formatCurrency(0) : (egFee ? formatCurrency(egFee) : '-')}
                            </td>
                            <td className="text-warning">
                              {stripeFee ? formatCurrency(stripeFee) : '-'}
                            </td>
                            <td className={`fw-bold ${netRemaining >= 0 ? 'text-success' : 'text-danger'}`}>
                              {formatCurrency(netRemaining)}
                            </td>
                            <td>{getStatusBadge(order.status)}</td>
                            <td>{formatDate(order.createdAt)}</td>
                            <td>
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOrderClick(order);
                                }}
                              >
                                <FaEye />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Payment Comparison Section */}
      {orderPayments.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card className="border-0 shadow-sm" style={{ borderLeft: `4px solid ${paymentComparison.difference === 0 ? '#28a745' : '#ffc107'}` }}>
              <Card.Header className={paymentComparison.difference === 0 ? "bg-success text-white" : "bg-warning text-dark"}>
                <h5 className="mb-0">
                  <FaExclamationTriangle className="me-2" />
                  Payment Verification (Orders vs order_payments)
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col md={4}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body>
                        <h5 className="text-primary">{formatCurrency(paymentComparison.ordersTotal)}</h5>
                        <p className="text-muted mb-0 small">Total from Orders</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body>
                        <h5 className="text-info">{formatCurrency(paymentComparison.paymentsTotal)}</h5>
                        <p className="text-muted mb-0 small">Total from order_payments</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className={`text-center border-0 ${paymentComparison.difference === 0 ? 'bg-success text-white' : 'bg-warning text-dark'}`}>
                      <Card.Body>
                        <h5 className={paymentComparison.difference === 0 ? "text-white" : "text-dark"}>
                          {formatCurrency(Math.abs(paymentComparison.difference))}
                        </h5>
                        <p className={paymentComparison.difference === 0 ? "text-white-50 mb-0 small" : "text-dark mb-0 small"}>
                          {paymentComparison.difference === 0 ? '✓ Match' : paymentComparison.difference > 0 ? 'Payments > Orders' : 'Orders > Payments'}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {paymentComparison.difference !== 0 && (
                  <Alert variant={paymentComparison.difference > 0 ? "warning" : "danger"}>
                    <strong>Discrepancy Detected!</strong> There is a difference of <strong>{formatCurrency(Math.abs(paymentComparison.difference))}</strong> between orders and payments.
                    {paymentComparison.difference > 0 && paymentComparison.paymentsNotInOrders.length > 0 && (
                      <div className="mt-2">
                        <strong>OrderIds from order_payments not found in orders ({paymentComparison.paymentsNotInOrders.length}):</strong>
                        <div className="mt-1">
                          {paymentComparison.paymentsNotInOrders.map((item, idx) => (
                            <Badge key={idx} bg="danger" className="me-1 mb-1">
                              {item.orderId} ({formatCurrency(item.amount)})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </Alert>
                )}

                {paymentComparison.missingPayments.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-danger mb-2">
                      Missing Payments ({paymentComparison.missingPayments.length})
                    </h6>
                    <p className="text-muted small mb-2">
                      Orders without corresponding entries in order_payments:
                    </p>
                    <div className="table-responsive">
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>User Email</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentComparison.missingPayments.map((item) => (
                            <tr key={item.orderId}>
                              <td><code>{item.orderUniqueId}</code></td>
                              <td>{item.userEmail}</td>
                              <td className="fw-bold text-success">{formatCurrency(item.amount)}</td>
                              <td>{getStatusBadge(item.status)}</td>
                              <td>{formatDate(item.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {paymentComparison.mismatchedPayments.length > 0 && (
                  <div className="mb-3">
                    <Alert variant="warning" className="mb-3">
                      <strong>⚠ Amount Mismatches Detected!</strong> Found <strong>{paymentComparison.mismatchedPayments.length}</strong> order(s) where payment totals don't match order amounts.
                    </Alert>
                    
                    {/* Summary of mismatches */}
                    {(() => {
                      const greaterPayments = paymentComparison.mismatchedPayments.filter(m => m.difference > 0);
                      const lessPayments = paymentComparison.mismatchedPayments.filter(m => m.difference < 0);
                      const greaterTotal = greaterPayments.reduce((sum, m) => sum + m.difference, 0);
                      const lessTotal = lessPayments.reduce((sum, m) => sum + Math.abs(m.difference), 0);
                      
                      return (
                        <Row className="mb-3">
                          {greaterPayments.length > 0 && (
                            <Col md={6}>
                              <Card className="border-0 bg-danger text-white">
                                <Card.Body className="p-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <strong>Payments Greater Than Orders</strong>
                                      <br />
                                      <small>{greaterPayments.length} order(s) - Total: {formatCurrency(greaterTotal)}</small>
                                    </div>
                                    <Badge bg="light" text="dark" style={{ fontSize: '1.2rem' }}>
                                      ↑
                                    </Badge>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                          {lessPayments.length > 0 && (
                            <Col md={6}>
                              <Card className="border-0 bg-warning text-dark">
                                <Card.Body className="p-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <strong>Payments Less Than Orders</strong>
                                      <br />
                                      <small>{lessPayments.length} order(s) - Total: {formatCurrency(lessTotal)}</small>
                                    </div>
                                    <Badge bg="dark" style={{ fontSize: '1.2rem' }}>
                                      ↓
                                    </Badge>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          )}
                        </Row>
                      );
                    })()}
                    
                    <div className="table-responsive">
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>User Email</th>
                            <th>Order Amount</th>
                            <th>Payment Total</th>
                            <th>Difference</th>
                            <th>Status</th>
                            <th>Payment Count</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentComparison.mismatchedPayments
                            .sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference)) // Sort by absolute difference, largest first
                            .map((item) => {
                              const isGreater = item.difference > 0;
                              const isLess = item.difference < 0;
                              
                              return (
                                <tr key={item.orderId} className={isGreater ? 'table-danger' : isLess ? 'table-warning' : ''}>
                                  <td><code>{item.orderUniqueId}</code></td>
                                  <td>{item.userEmail}</td>
                                  <td className="fw-bold text-success">
                                    {formatCurrency(item.orderAmount)}
                                  </td>
                                  <td className="fw-bold text-info">
                                    {formatCurrency(item.paymentTotal)}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {isGreater && (
                                        <Badge bg="danger" className="me-2">
                                          ↑ {formatCurrency(item.difference)} MORE
                                        </Badge>
                                      )}
                                      {isLess && (
                                        <Badge bg="warning" text="dark" className="me-2">
                                          ↓ {formatCurrency(Math.abs(item.difference))} LESS
                                        </Badge>
                                      )}
                                      {!isGreater && !isLess && (
                                        <span className="text-muted">-</span>
                                      )}
                                    </div>
                                  </td>
                                  <td>{getStatusBadge(item.status)}</td>
                                  <td>
                                    <Badge bg="secondary">{item.paymentCount} payment(s)</Badge>
                                  </td>
                                  <td>{formatDate(item.createdAt)}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {paymentComparison.extraPayments.length > 0 && (
                  <div className="mb-3">
                    <Alert variant="danger" className="mb-2">
                      <strong>⚠ Payments Not Counted in Orders!</strong> The following <strong>{paymentComparison.extraPayments.length}</strong> payment(s) have orderIds that don't exist in the orders collection. These payments are included in the order_payments total but NOT in the orders total.
                    </Alert>
                    <div className="table-responsive">
                      <Table striped hover size="sm">
                        <thead>
                          <tr>
                            <th>Payment ID</th>
                            <th>Order ID (Missing in Orders)</th>
                            <th>Amount</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentComparison.extraPayments.map((item) => (
                            <tr key={item.paymentId} className="table-danger">
                              <td><code>{item.paymentId}</code></td>
                              <td>
                                <Badge bg="danger">
                                  <code>{item.orderId}</code>
                                </Badge>
                                <small className="text-muted d-block mt-1">This orderId is not in orders collection</small>
                              </td>
                              <td className="fw-bold text-danger">{formatCurrency(item.amount)}</td>
                              <td>{formatDate(item.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="mt-2 p-2 bg-light rounded">
                      <strong>Summary:</strong> Total amount from payments not in orders: <strong className="text-danger">{formatCurrency(paymentComparison.extraPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}</strong>
                    </div>
                  </div>
                )}

                {paymentComparison.difference === 0 && 
                 paymentComparison.missingPayments.length === 0 && 
                 paymentComparison.extraPayments.length === 0 &&
                 paymentComparison.mismatchedPayments.length === 0 && (
                  <Alert variant="success">
                    <strong>✓ All payments match!</strong> All orders have corresponding entries in order_payments, and all totals match.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Orders Section */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">All Orders ({filteredOrders.length})</h5>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={handleBulkSyncFees}
                    disabled={bulkSyncing || orders.length === 0}
                    title="Reload EG fee and Stripe fee for orders missing rates"
                  >
                    {bulkSyncing ? `Reloading Fees (${bulkSyncedCount}/${bulkSyncTotal})` : 'Reload Fees'}
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={handlePayoutClick}
                    disabled={orders.length === 0}
                  >
                    Payout
                  </Button>
                  <CSVLink 
                    data={csvData} 
                    filename={`event-${eventId}-orders.csv`}
                    className="btn btn-outline-success btn-sm"
                  >
                    <FaDownload className="me-1" />
                    Export CSV
                  </CSVLink>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Search and Filter */}
              <Row className="mb-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search orders..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={3}>
                  <Form.Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="refunded">Refunded</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Orders Table */}
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>User Email</th>
                      <th>Amount</th>
                      <th>EG Fee</th>
                      <th>Stripe Fee</th>
                      <th>Status</th>
                      <th>UTM Source</th>
                      <th>UTM Medium</th>
                      <th>UTM Campaign</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((order) => {
                      const metadata = order.viewData?.metadata || {};
                      const utmSource = metadata.utm_source || metadata.utmSource || '-';
                      const utmMedium = metadata.utm_medium || metadata.utmMedium || '-';
                      const utmCampaign = metadata.utm_campaign || metadata.utmCampaign || '-';
                      
                      return (
                        <tr key={order.id} style={{ cursor: 'pointer' }} onClick={() => handleOrderClick(order)}>
                          <td>
                            <code>{order.uniqueId || order.id}</code>
                          </td>
                          <td>{order.userEmail || 'N/A'}</td>
                          <td className="fw-bold text-success">
                            <div className="d-flex align-items-center">
                              {formatCurrency(order.amount)}
                              {hasMissingFees(order) && (
                                <>
                                  <FaExclamationTriangle 
                                    className="ms-2 text-warning" 
                                    title="Missing fees - amount > 0 but egFee or stripeFee is 0"
                                    style={{ fontSize: '14px' }}
                                  />
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="ms-2 sync-button"
                                    onClick={(e) => handleSyncFees(order.id, e)}
                                    title="Sync service fees"
                                    style={{ padding: '2px 6px', fontSize: '12px' }}
                                  >
                                    <FaSync />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="text-warning">
                            {order.fees?.egFee ? formatCurrency(order.fees.egFee) : '-'}
                          </td>
                          <td className="text-warning">
                            {order.fees?.stripeFee ? formatCurrency(order.fees.stripeFee) : '-'}
                          </td>
                          <td>{getStatusBadge(order.status)}</td>
                          <td className="small">{utmSource}</td>
                          <td className="small">{utmMedium}</td>
                          <td className="small">{utmCampaign}</td>
                          <td>{formatDate(order.createdAt)}</td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOrderClick(order);
                              }}
                            >
                              <FaEye />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center">
                  <Pagination>
                    <Pagination.Prev 
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, index) => (
                      <Pagination.Item
                        key={index + 1}
                        active={currentPage === index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next 
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}

              {filteredOrders.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No orders found matching your criteria.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Seats taken (from seats perspective): seat has ticketId -> ticketId, orderId, order email, ticket userName */}
      <Row className="mb-4">
        <Col>
          <Card className="border-info shadow-sm">
            <Card.Header className="bg-info bg-opacity-10">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                Seats (pending, reserved, taken)
                {seatsTakenList.length > 0 && (
                  <Badge bg="info" className="ms-2">{seatsTakenList.length}</Badge>
                )}
              </h5>
                {seatsTakenList.length > 0 && (
                  <CSVLink
                    data={seatsTakenList.map((row) => {
                      const obj = {
                        'Seat ID': row.seatId,
                        'seatName': row.seatName ?? '',
                        'ticketId': row.ticketId ?? '',
                        'orderId': row.orderId ?? '',
                        'Order email': row.orderEmail ?? '',
                        'userName (order)': row.userName ?? ''
                      };
                      eventForms.forEach((form, i) => {
                        obj[form.title] = (row.formValues || [])[i] ?? '';
                      });
                      return obj;
                    })}
                    headers={[
                      { label: 'Seat ID', key: 'Seat ID' },
                      { label: 'seatName', key: 'seatName' },
                      { label: 'ticketId', key: 'ticketId' },
                      { label: 'orderId', key: 'orderId' },
                      { label: 'Order email', key: 'Order email' },
                      { label: 'userName (order)', key: 'userName (order)' },
                      ...eventForms.map((f) => ({ label: f.title, key: f.title }))
                    ]}
                    filename={`seats-pending-reserved-taken-${eventData?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'event'}-${new Date().toISOString().split('T')[0]}.csv`}
                    className="btn btn-outline-success btn-sm"
                  >
                    <FaDownload className="me-1" />
                    Download
                  </CSVLink>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-2">
                Seats with status <strong>pending</strong>, <strong>reserved</strong>, or <strong>taken</strong> from <code>events/{eventId}/seats</code>. For reserved or pending only seat info is shown; for taken, ticket, order, and form data are shown.
              </p>
              {loadingAttendees ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading...</div>
              ) : seatsTakenList.length === 0 ? (
                <p className="text-muted mb-0">No seats with status pending, reserved, or taken.</p>
              ) : (
                <Table size="sm" striped bordered responsive>
                  <thead>
                    <tr>
                      <th>Seat ID</th>
                      <th>seatName</th>
                      <th>ticketId</th>
                      <th>orderId</th>
                      <th>Order email</th>
                      <th>userName (order)</th>
                      {eventForms.map((form) => (
                        <th key={form.id} className="small">{form.title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {seatsTakenList.map((row) => (
                      <tr key={row.seatId}>
                        <td><code className="small">{row.seatId}</code></td>
                        <td className="small">{row.seatName ?? '—'}</td>
                        <td><code className="small">{row.ticketId}</code></td>
                        <td><code className="small">{row.orderId}</code></td>
                        <td className="small">{row.orderEmail}</td>
                        <td className="small">{row.userName ?? '—'}</td>
                        {eventForms.map((form, i) => (
                          <td key={form.id} className="small">{(row.formValues || [])[i] ?? '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Seats that have ticketId but the ticket does not show seatId */}
      <Row className="mb-4">
        <Col>
          <Card className="border-warning shadow-sm">
            <Card.Header className="bg-warning bg-opacity-10">
              <h5 className="mb-0">
                Seats with ticketId but ticket has no seatId
                {seatsWithMissingTicketSeatId.length > 0 && (
                  <Badge bg="warning" className="ms-2">{seatsWithMissingTicketSeatId.length}</Badge>
                )}
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-2">
                Seats from <code>events/{eventId}/seats</code> that have a <code>ticketId</code>, where the linked ticket does not have <code>seatId</code> or <code>seat</code> set.
              </p>
              {loadingAttendees ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" /> Loading...
                </div>
              ) : seatsWithMissingTicketSeatId.length === 0 ? (
                <p className="text-muted mb-0">None. All seats with a ticketId have a matching ticket that shows seatId/seat.</p>
              ) : (
                <Table size="sm" striped bordered responsive>
                  <thead>
                    <tr>
                      <th>Seat ID</th>
                      <th>ticketId</th>
                      <th>Other seat fields</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seatsWithMissingTicketSeatId.map((seat) => (
                      <tr key={seat.id}>
                        <td><code className="small">{seat.id}</code></td>
                        <td><code className="small">{seat.ticketId}</code></td>
                        <td>
                          <small className="text-muted">
                            {Object.entries(seat)
                              .filter(([k]) => k !== 'id' && k !== 'ticketId')
                              .map(([k, v]) => {
                                let str = '';
                                if (v == null) str = '';
                                else if (typeof v === 'object' && typeof v.toDate === 'function') str = v.toDate().toISOString?.() ?? String(v);
                                else if (typeof v === 'object') str = JSON.stringify(v);
                                else str = String(v);
                                return str ? `${k}: ${str}` : '';
                              })
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <hr className="my-4" />
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h6 className="mb-0">Tickets without seatId</h6>
                {ticketsWithoutSeatId.length > 0 && (
                  <CSVLink
                    data={ticketsWithoutSeatId.map((t) => ({
                      userName: t.userName ?? '',
                      TicketId: t.id,
                      OrderId: t.orderId ?? '',
                      ticketMethod: t.ticketMethod ?? '',
                      timestamp: t.timestamp ? formatDate(t.timestamp?.toDate?.() ?? t.timestamp) : ''
                    }))}
                    headers={[
                      { label: 'userName', key: 'userName' },
                      { label: 'TicketId', key: 'TicketId' },
                      { label: 'OrderId', key: 'OrderId' },
                      { label: 'ticketMethod', key: 'ticketMethod' },
                      { label: 'timestamp', key: 'timestamp' }
                    ]}
                    filename={`tickets-without-seatId-${eventData?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'event'}-${new Date().toISOString().split('T')[0]}.csv`}
                    className="btn btn-outline-success btn-sm"
                  >
                    <FaDownload className="me-1" />
                    Export list
                  </CSVLink>
                )}
              </div>
              <p className="text-muted small mb-2">
                Tickets from <code>events/{eventId}/tickets</code> that do not have <code>seatId</code> or <code>seat</code> set.
              </p>
              {loadingAttendees ? (
                <div className="text-center py-2"><Spinner animation="border" size="sm" /> Loading...</div>
              ) : ticketsWithoutSeatId.length === 0 ? (
                <p className="text-muted mb-0 small">None. All tickets have seatId/seat set.</p>
              ) : (
                <Table size="sm" striped bordered responsive>
                  <thead>
                    <tr>
                      <th>userName</th>
                      <th>TicketId</th>
                      <th>OrderId</th>
                      <th>ticketMethod</th>
                      <th>timestamp</th>
                      <th>Assign seat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketsWithoutSeatId.map((t) => (
                      <tr key={t.id}>
                        <td>{t.userName ?? '—'}</td>
                        <td><code className="small">{t.id}</code></td>
                        <td><code className="small">{t.orderId ?? '—'}</code></td>
                        <td>{t.ticketMethod ?? '—'}</td>
                        <td className="small">{t.timestamp ? formatDate(t.timestamp?.toDate?.() ?? t.timestamp) : '—'}</td>
                        <td>
                          <InputGroup size="sm" className="flex-nowrap">
                            <Form.Control
                              placeholder="seatId"
                              value={assignSeatInputMap[t.id] ?? ''}
                              onChange={(e) => setAssignSeatInputMap(prev => ({ ...prev, [t.id]: e.target.value }))}
                              className="small"
                            />
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={assigningSeatTicketId === t.id}
                              onClick={() => assignSeatToTicket(t.id, assignSeatInputMap[t.id])}
                            >
                              {assigningSeatTicketId === t.id ? '...' : 'Assign'}
                            </Button>
                          </InputGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Attendees Section */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Event Tickets</h5>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="info">{attendees.length} Tickets</Badge>
                  <CSVLink
                    data={generateTicketsCSVData()}
                    filename={`event-tickets-${eventData?.title?.replace(/[^a-zA-Z0-9]/g, '-') || 'event'}-${new Date().toISOString().split('T')[0]}.csv`}
                    className="btn btn-outline-success btn-sm"
                  >
                    <FaDownload className="me-1" />
                    Export CSV
                  </CSVLink>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {attendees.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Badge bg="primary">Total: {ticketStats.total}</Badge>
                    <Badge bg="success">Checked-in: {ticketStats.checkedIn}</Badge>
                    <Badge bg="info">With Seat: {ticketStats.withSeat}</Badge>
                    <Badge bg="dark">Check-in Rate: {(ticketStats.rate * 100).toFixed(1)}%</Badge>
                  </div>
                  {Object.keys(ticketStats.byType).length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {Object.entries(ticketStats.byType).map(([type, count]) => (
                        <Badge key={type} bg="secondary">{type}: {count}</Badge>
                      ))}
                    </div>
                  )}
                  {Object.keys(ticketStats.byTypePaidFree).length > 0 && (
                    <div className="mt-2">
                      <h6 className="mb-1">Tickets by Type (Paid vs Free)</h6>
                      <Table size="sm" bordered responsive>
                        <thead>
                          <tr>
                            <th>Ticket Type</th>
                            <th className="text-end">Paid</th>
                            <th className="text-end">Free</th>
                            <th className="text-end">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(ticketStats.byTypePaidFree)
                            .sort((a,b) => b[1].total - a[1].total)
                            .map(([type, data]) => (
                            <tr key={type}>
                              <td>{type}</td>
                              <td className="text-end">{data.paid}</td>
                              <td className="text-end">{data.free}</td>
                              <td className="text-end">{data.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  {Object.keys(couponStats).length > 0 && (
                    <div className="mt-2">
                      <h6 className="mb-1">Coupons Usage</h6>
                      <Table size="sm" bordered responsive>
                        <thead>
                          <tr>
                            <th>Coupon Code</th>
                            <th className="text-end">Orders</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(couponStats)
                            .sort((a,b) => b[1] - a[1])
                            .map(([code, count]) => (
                            <tr key={code}>
                              <td><code>{code}</code></td>
                              <td className="text-end">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                  {ticketStats.checkedIn > 0 && Object.keys(ticketStats.byHour).length > 0 && (
                    <div className="mt-2">
                      <h6 className="mb-1">Check-ins by Hour</h6>
                      <Table size="sm" bordered responsive>
                        <thead>
                          <tr>
                            <th>Hour</th>
                            <th className="text-end">Check-ins</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(ticketStats.byHour)
                            .sort((a,b) => new Date(a[0]) - new Date(b[0]))
                            .map(([hour, count]) => (
                            <tr key={hour}>
                              <td>{hour}</td>
                              <td className="text-end">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
              {loadingAttendees ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2">Loading tickets...</p>
                </div>
              ) : attendees.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No tickets found for this event.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Ticket ID</th>
                        <th>Order ID</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Ticket Type</th>
                        <th>Paid?</th>
                        <th>Seat</th>
                        <th>Order Status</th>
                        <th>Check-in</th>
                        <th>Check-in Date</th>
                        {eventForms.map((form) => (
                          <th key={form.id}>{form.title}</th>
                        ))}
                        <th>Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendees.map((attendee) => (
                        <tr key={attendee.id}>
                          <td>
                            <code className="text-info">{attendee.id}</code>
                          </td>
                          <td>
                            <code className="text-primary">{attendee.orderId}</code>
                          </td>
                          <td>{attendee.firstName}</td>
                          <td>{attendee.lastName}</td>
                          <td>{attendee.email}</td>
                          <td>
                            <Badge bg="secondary">
                              {pricesMap.get(attendee.ticketType)?.priceDescription || attendee.ticketType}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={attendee.isFree ? 'secondary' : 'success'}>
                              {attendee.isFree ? 'Free' : 'Paid'}
                            </Badge>
                          </td>
                          <td>
                            {attendee.seat ? (
                              <span className="text-info">
                                {attendee.seat}
                              </span>
                            ) : (
                              <span className="text-muted">No seat assigned</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={attendee.orderStatus === 'completed' ? 'success' : 
                                     attendee.orderStatus === 'pending' ? 'warning' : 
                                     attendee.orderStatus === 'cancelled' ? 'danger' : 'secondary'}>
                              {attendee.orderStatus}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={attendee.checkin ? 'success' : 'secondary'}>
                              {attendee.checkin ? 'Yes' : 'No'}
                            </Badge>
                          </td>
                          <td>
                            {attendee.checkinDate ? formatDate(attendee.checkinDate) : '-'}
                          </td>
                          {eventForms.map((form) => {
                            const formSubmission = attendee.processedForms?.find(f => f.id === form.id);
                            return (
                              <td key={form.id}>
                                {formSubmission ? (
                                  <span className="text-dark">{formSubmission.value}</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td>{formatDate(attendee.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Details Modal */}
      <Modal show={showOrderModal} onHide={() => setShowOrderModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Order ID:</strong><br />
                  <code>{selectedOrder.uniqueId || selectedOrder.id}</code>
                </Col>
                <Col md={6}>
                  <strong>Status:</strong><br />
                  {getStatusBadge(selectedOrder.status)}
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <strong>User Email:</strong><br />
                  {selectedOrder.userEmail || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Amount:</strong><br />
                  <span className="fw-bold text-success">
                    {formatCurrency(selectedOrder.amount)}
                  </span>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Created:</strong><br />
                  {formatDate(selectedOrder.createdAt)}
                </Col>
                <Col md={6}>
                  <strong>Payment Method:</strong><br />
                  {selectedOrder.paymentMethod || 'N/A'}
                </Col>
              </Row>
              
              {/* Fees Section */}
              {selectedOrder.fees && (selectedOrder.fees.egFee || selectedOrder.fees.stripeFee) && (
                <div className="mb-3">
                  <strong>Fees:</strong>
                  <div className="mt-2">
                    <Row>
                      {selectedOrder.fees.egFee && (
                        <Col md={6}>
                          <div className="d-flex justify-content-between">
                            <span>EG Fee:</span>
                            <span className="fw-bold text-warning">
                              {formatCurrency(selectedOrder.fees.egFee)}
                            </span>
                          </div>
                        </Col>
                      )}
                      {selectedOrder.fees.stripeFee && (
                        <Col md={6}>
                          <div className="d-flex justify-content-between">
                            <span>Stripe Fee:</span>
                            <span className="fw-bold text-warning">
                              {formatCurrency(selectedOrder.fees.stripeFee)}
                            </span>
                          </div>
                        </Col>
                      )}
                    </Row>
                    <Row className="mt-2">
                      <Col md={12}>
                        <div className="d-flex justify-content-between">
                          <span className="fw-bold">Total Fees:</span>
                          <span className="fw-bold text-warning">
                            {formatCurrency((selectedOrder.fees.egFee || 0) + (selectedOrder.fees.stripeFee || 0))}
                          </span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              )}
              
              {selectedOrder.cartItems && selectedOrder.cartItems.length > 0 && (
                <div className="mb-3">
                  <strong>Items:</strong>
                  <ListGroup className="mt-2">
                    {selectedOrder.cartItems.map((item, index) => (
                      <ListGroup.Item key={index}>
                        <div className="d-flex justify-content-between">
                          <span>{item.priceDescription || 'Item'}</span>
                          <span>Qty: {item.quantity} - {formatCurrency(item.price)}</span>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
              
              {/* UTM Tracking Parameters */}
              {selectedOrder.viewData?.metadata && (
                <div className="mb-3">
                  <strong>UTM Tracking Parameters:</strong>
                  <div className="mt-2">
                    <Table size="sm" bordered>
                      <tbody>
                        {selectedOrder.viewData.metadata.utm_source && (
                          <tr>
                            <td><strong>UTM Source:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utm_source}</td>
                          </tr>
                        )}
                        {selectedOrder.viewData.metadata.utm_medium && (
                          <tr>
                            <td><strong>UTM Medium:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utm_medium}</td>
                          </tr>
                        )}
                        {selectedOrder.viewData.metadata.utm_campaign && (
                          <tr>
                            <td><strong>UTM Campaign:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utm_campaign}</td>
                          </tr>
                        )}
                        {selectedOrder.viewData.metadata.utm_term && (
                          <tr>
                            <td><strong>UTM Term:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utm_term}</td>
                          </tr>
                        )}
                        {selectedOrder.viewData.metadata.utm_content && (
                          <tr>
                            <td><strong>UTM Content:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utm_content}</td>
                          </tr>
                        )}
                        {/* Also check for camelCase versions */}
                        {!selectedOrder.viewData.metadata.utm_source && selectedOrder.viewData.metadata.utmSource && (
                          <tr>
                            <td><strong>UTM Source:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utmSource}</td>
                          </tr>
                        )}
                        {!selectedOrder.viewData.metadata.utm_medium && selectedOrder.viewData.metadata.utmMedium && (
                          <tr>
                            <td><strong>UTM Medium:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utmMedium}</td>
                          </tr>
                        )}
                        {!selectedOrder.viewData.metadata.utm_campaign && selectedOrder.viewData.metadata.utmCampaign && (
                          <tr>
                            <td><strong>UTM Campaign:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utmCampaign}</td>
                          </tr>
                        )}
                        {!selectedOrder.viewData.metadata.utm_term && selectedOrder.viewData.metadata.utmTerm && (
                          <tr>
                            <td><strong>UTM Term:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utmTerm}</td>
                          </tr>
                        )}
                        {!selectedOrder.viewData.metadata.utm_content && selectedOrder.viewData.metadata.utmContent && (
                          <tr>
                            <td><strong>UTM Content:</strong></td>
                            <td>{selectedOrder.viewData.metadata.utmContent}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOrderModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Payout Summary Modal */}
      <Modal show={showPayoutModal} onHide={() => setShowPayoutModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Payout Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="payout-summary">
            <h6 className="mb-4">Financial Breakdown for Event: {eventData?.title}</h6>
            
            {payoutSummary.ordersWithMissingFees > 0 && (
              <Alert variant="warning" className="mb-4">
                <FaExclamationTriangle className="me-2" />
                <strong>Warning:</strong> {payoutSummary.ordersWithMissingFees} order(s) have missing fee data. 
                Please review and update the fee information before processing payout.
              </Alert>
            )}
            
            <Row className="mb-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <h5 className="text-primary">{formatCurrency(payoutSummary.totalAmount)}</h5>
                    <p className="text-muted mb-0">Total Amount</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <h5 className="text-warning">{formatCurrency(payoutSummary.totalEgFee)}</h5>
                    <p className="text-muted mb-0">Total EG Fees</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <h5 className="text-warning">{formatCurrency(payoutSummary.totalStripeFee)}</h5>
                    <p className="text-muted mb-0">Total Stripe Fees</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="border-0 shadow-sm bg-success text-white">
                  <Card.Body className="text-center">
                    <h5 className="text-white">{formatCurrency(payoutSummary.netPayout)}</h5>
                    <p className="text-white-50 mb-0">Net Payout</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <div className="mt-4 p-3 bg-light rounded">
              <h6>Calculation Details:</h6>
              <div className="d-flex justify-content-between">
                <span>Total Revenue:</span>
                <span>{formatCurrency(payoutSummary.totalAmount)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Total EG Fees:</span>
                <span className="text-warning">-{formatCurrency(payoutSummary.totalEgFee)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Total Stripe Fees:</span>
                <span className="text-warning">-{formatCurrency(payoutSummary.totalStripeFee)}</span>
              </div>
                <div className="d-flex justify-content-between">
                  <span>Past Payouts:</span>
                  <span className="text-muted">-{formatCurrency(payoutSummary.pastPayoutsTotal || 0)}</span>
                </div>
              <hr />
              <div className="d-flex justify-content-between fw-bold">
                  <span>Remaining Funds:</span>
                  <span className="text-success">{formatCurrency(payoutSummary.remainingFunds || 0)}</span>
              </div>
            </div>

            <Card className="mt-4 border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h6 className="mb-0">Generate Payout Receipt</h6>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptTotalOrders">
                      <Form.Label>Total Orders</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        value={payoutReceiptForm.totalOrders}
                        onChange={(e) => setPayoutReceiptForm(prev => ({ ...prev, totalOrders: e.target.value }))}
                        placeholder="0"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptTotalOrderedAmount">
                      <Form.Label>Total Ordered Amount</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={payoutReceiptForm.totalOrderedAmount}
                        onChange={(e) => {
                          const totalOrderedAmount = Number(e.target.value) || 0;
                          const serviceFeeCustomerPay = Number(payoutReceiptForm.serviceFeeCustomerPay) || 0;
                          const taxAmount = Number(payoutReceiptForm.taxAmount) || 0;
                          setPayoutReceiptForm(prev => ({
                            ...prev,
                            totalOrderedAmount: e.target.value,
                            totalAmount: roundTo2(totalOrderedAmount + taxAmount),
                            netPayout: roundTo2(totalOrderedAmount + taxAmount - serviceFeeCustomerPay)
                          }));
                        }}
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptTotalAmount">
                      <Form.Label>Total Amount</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={payoutReceiptForm.totalAmount}
                        readOnly
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptServiceFeeCustomerPay">
                      <Form.Label>Service Fee (Customer Pay)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={payoutReceiptForm.serviceFeeCustomerPay}
                        onChange={(e) => {
                          const totalOrderedAmount = Number(payoutReceiptForm.totalOrderedAmount) || 0;
                          const serviceFeeCustomerPay = Number(e.target.value) || 0;
                          const taxAmount = Number(payoutReceiptForm.taxAmount) || 0;
                          setPayoutReceiptForm(prev => ({
                            ...prev,
                            serviceFeeCustomerPay: e.target.value,
                            totalAmount: roundTo2(totalOrderedAmount + taxAmount),
                            netPayout: roundTo2(totalOrderedAmount + taxAmount - serviceFeeCustomerPay)
                          }));
                        }}
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptTaxAmount">
                      <Form.Label>Tax</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={payoutReceiptForm.taxAmount}
                        onChange={(e) => {
                          const totalOrderedAmount = Number(payoutReceiptForm.totalOrderedAmount) || 0;
                          const serviceFeeCustomerPay = Number(payoutReceiptForm.serviceFeeCustomerPay) || 0;
                          const taxAmount = Number(e.target.value) || 0;
                          setPayoutReceiptForm(prev => ({
                            ...prev,
                            taxAmount: e.target.value,
                            totalAmount: roundTo2(totalOrderedAmount + taxAmount),
                            netPayout: roundTo2(totalOrderedAmount + taxAmount - serviceFeeCustomerPay)
                          }));
                        }}
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptPaymentFeeCustomerPay">
                      <Form.Label>Payment Fee (Customer Pay)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={payoutReceiptForm.paymentFeeCustomerPay}
                        onChange={(e) => setPayoutReceiptForm(prev => ({ ...prev, paymentFeeCustomerPay: e.target.value }))}
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="payoutReceiptNetPayout">
                      <Form.Label>Net Payout</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={payoutReceiptForm.netPayout}
                        onChange={(e) => setPayoutReceiptForm(prev => ({ ...prev, netPayout: e.target.value }))}
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

              {pastPayouts && pastPayouts.length > 0 && (
                <div className="mt-4">
                  <h6 className="mb-3">Past Payouts</h6>
                  <div className="table-responsive">
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Payout ID</th>
                          <th className="text-end">Amount</th>
                          <th className="text-center">Status</th>
                          <th className="text-end">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...pastPayouts]
                          .sort((a, b) => {
                            const ad = a.createdAt?.toDate?.() ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                            const bd = b.createdAt?.toDate?.() ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                            return bd - ad;
                          })
                          .map((p) => {
                            const created = p.createdAt?.toDate?.() ? p.createdAt.toDate() : (p.createdAt ? new Date(p.createdAt) : null);
                            const amt = p?.totals?.netPayout || 0;
                            return (
                              <tr key={p.id}>
                                <td>
                                  <code className="text-muted">{p.id}</code>
                                </td>
                                <td className="text-end">
                                  {formatCurrency(amt)}
                                </td>
                                <td className="text-center">
                                  <Badge bg={p.status === 'pending' ? 'warning' : p.status === 'cancelled' ? 'danger' : 'success'}>
                                    {p.status || 'unknown'}
                                  </Badge>
                                </td>
                                <td className="text-end small">
                                  {created ? formatDate(created) : '-'}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPayoutModal(false)}>
            Close
          </Button>
          <Button 
            variant="info" 
            onClick={generatePayoutImage}
            className="me-2"
          >
            <FaDownload className="me-2" />
            Generate Image
          </Button>
          <Button 
            variant="success" 
            onClick={handleProcessPayout}
            disabled={payoutSummary.ordersWithMissingFees > 0 || processingPayout}
            title={payoutSummary.ordersWithMissingFees > 0 ? "Cannot process payout with missing fee data" : "Process payout"}
          >
            {processingPayout ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Processing...
              </>
            ) : (
              'Process Payout'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
